// Кадр анимации «Траекторий по HSI»: кометы, бегущие по усикам.
//
// Здесь — только чистая математика (фаза, положение головы, срез ломаной). Стиль
// слоёв — в map/hsiComet.ts, склейка с картой — в ModelsPage. Геометрия усика
// приходит с бэкенда целиком (origin→now, а после Фазы 2 — и прогнозное плечо),
// сеть при анимации не дёргается.

import type { HsiDriftCollection } from '../api'

/** Длительность одного пролёта кометы по усику. */
export const COMET_CYCLE_MS = 2000
/** Длина хвоста в долях длины усика. */
export const COMET_TAIL = 0.34
/** На сколько звеньев режем хвост (шагов затухания). */
export const COMET_STEPS = 6
/** Потолок комет в кадре: выше — кадр дороже, а картинка уже не читается. */
export const MAX_COMETS = 900

/** Детерминированная «случайная» фаза усика (хеш ключа → [0,1)).
 *
 *  Именно она даёт разнобой: у каждого усика своя точка старта в цикле. Хеш, а не
 *  Math.random, — чтобы фаза не прыгала при перерисовке компонента. Ключ берётся
 *  от самого усика (его origin), а не от места в списке: иначе прореживание и
 *  любой будущий фильтр перетасовывали бы всю картину фаз. */
export function cometPhase(key: number): number {
  let x = Math.imul(key ^ 0x9e3779b9, 0x85ebca6b)
  x ^= x >>> 13
  x = Math.imul(x, 0xc2b2ae35)
  x ^= x >>> 16
  return (x >>> 0) / 4294967296
}

/** Кумулятивная длина ломаной, нормированная на полную (последняя = 1).
 *
 *  Длина меряется в ЭКРАННЫХ пропорциях, а не в сырых градусах. */
export function cumulative(coords: [number, number][]): number[] {
  const cum = [0]
  let total = 0
  for (let i = 1; i < coords.length; i++) {
    // Долгота приходит с бэкенда в НЕПРЕРЫВНОЙ восточной шкале (to_east в
    // src/cabinet/hsi_drift.py): шва 180° здесь нет, скачка на −360° быть не может.
    const dLon = coords[i][0] - coords[i - 1][0]
    const dLat = coords[i][1] - coords[i - 1][1]
    // Экран — Web Mercator: градус широты занимает 1/cos(lat) градуса долготы.
    // Без поправки комета разгоняется на меридиональных коленах (×2 на 60° с.ш.).
    const latMid = (coords[i][1] + coords[i - 1][1]) * 0.5
    total += Math.hypot(dLon, dLat / Math.cos((latMid * Math.PI) / 180))
    cum.push(total)
  }
  if (total <= 0) return coords.map(() => 0)
  for (let i = 0; i < cum.length; i++) cum[i] /= total
  return cum
}

/** Точка на ломаной по доле длины s ∈ [0,1] (края зажимаются). */
export function pointAt(
  coords: [number, number][],
  cum: number[],
  s: number,
): [number, number] {
  // Зовётся из requestAnimationFrame: исключение на вырожденной геометрии
  // погасило бы анимацию на всю сессию.
  if (coords.length < 2) return coords[0] ?? [0, 0]
  if (s <= 0) return coords[0]
  if (s >= 1) return coords[coords.length - 1]
  let k = 1
  while (k < cum.length - 1 && cum[k] < s) k++
  const span = cum[k] - cum[k - 1] || 1
  const f = (s - cum[k - 1]) / span
  return [
    coords[k - 1][0] + (coords[k][0] - coords[k - 1][0]) * f,
    coords[k - 1][1] + (coords[k][1] - coords[k - 1][1]) * f,
  ]
}

/** Кусок ломаной между долями s0 и s1 (с промежуточными вершинами).
 *  Вырожденный кусок (нулевой длины или перевёрнутый) → null. */
export function slicePolyline(
  coords: [number, number][],
  cum: number[],
  s0: number,
  s1: number,
): [number, number][] | null {
  // Рисовать нечего: вырожденная ломаная или чужая/битая кумулятива.
  if (coords.length < 2 || cum.length !== coords.length) return null
  const a = Math.max(0, Math.min(1, s0))
  const b = Math.max(0, Math.min(1, s1))
  if (b - a <= 1e-9) return null
  const out: [number, number][] = [pointAt(coords, cum, a)]
  for (let k = 0; k < cum.length; k++) {
    if (cum[k] > a && cum[k] < b) out.push(coords[k])
  }
  out.push(pointAt(coords, cum, b))
  return out
}

/** Положение головы кометы в долях усика.
 *
 *  `clock` ∈ [0,1) — общий такт. Голова стартует за началом усика (−TAIL: комета
 *  ещё не вошла) и доезжает до 1+TAIL — то есть уходит ЗА конец трассы, и хвост
 *  втягивается за горизонт следом, а не гаснет весь разом. */
export function cometHead(clock: number, phase: number): number {
  const q = (clock + phase) % 1
  return q * (1 + 2 * COMET_TAIL) - COMET_TAIL
}

export interface CometTrail {
  coords: [number, number][]
  cum: number[]
  hsi: number
  phase: number
}

export interface CometProps {
  hsi: number
  /** Яркость звена: 1 у головы, к хвосту падает. */
  a: number
  /** 1 — головное звено (белый кончик), 0 — тело хвоста. */
  tip: number
}

/** Выборка усиков → готовые к анимации трассы (дуга считается ОДИН раз).
 *
 *  Потолок MAX_COMETS: кадр собирается на CPU, и его цена линейна по числу комет.
 *  Потолок ПРОРЕЖИВАЕТ выборку равномерным шагом, а не режет её начало: сиды
 *  приходят физическим порядком (по geo9_id — широтными полосами, плюс склейка
 *  половин 180-го шва), и отсечка уложила бы все кометы одной полосой в углу. */
export function prepareTrails(fc: HsiDriftCollection): CometTrail[] {
  const trails: CometTrail[] = []
  const step = Math.max(1, Math.ceil(fc.features.length / MAX_COMETS))
  for (let i = 0; i < fc.features.length; i += step) {
    const feat = fc.features[i]
    const coords = feat.geometry.coordinates as [number, number][]
    if (!coords || coords.length < 2) continue
    const cum = cumulative(coords)
    // Замороженные («выброшенные на берег») усики отсекаются здесь же нулевой
    // дугой — флаг beached из properties для этого не нужен.
    // Именно «не больше нуля», а не «<= 0»: битая координата даёт NaN, и
    // сравнение NaN <= 0 ложно — усик пролез бы в кадр с NaN-геометрией.
    if (!(cum[cum.length - 1] > 0)) continue
    // Ключ фазы — округлённый origin трассы: он держится за сам усик, поэтому
    // при другом прореживании (или фильтре) фазы не перетасовываются.
    const key =
      (Math.round(coords[0][0] * 1000) * 73856093) ^ (Math.round(coords[0][1] * 1000) * 19349663)
    trails.push({
      coords,
      cum,
      hsi: Number(feat.properties.hsi) || 0,
      phase: cometPhase(key),
    })
  }
  return trails
}

/** Кадр анимации: каждая комета — до COMET_STEPS звеньев вдоль своей ломаной.
 *
 *  Звенья нарезаются от головы назад, яркость падает по удалению. Куски, вышедшие
 *  за конец усика, просто не попадают в кадр — так комета и «скрывается за
 *  горизонтом»: сначала пропадает голова, следом втягивается хвост.
 *
 *  Выдаваемая геометрия ПЕРЕИСПОЛЬЗУЕТ массивы вершин исходных трасс (промежуточные
 *  точки кладутся в срез по ссылке) — править их на месте нельзя. */
export function buildCometFrame(
  trails: CometTrail[],
  clock: number,
): GeoJSON.FeatureCollection<GeoJSON.LineString, CometProps> {
  const features: GeoJSON.Feature<GeoJSON.LineString, CometProps>[] = []
  for (const tr of trails) {
    const head = cometHead(clock, tr.phase)
    if (head <= 0 || head - COMET_TAIL >= 1) continue
    for (let k = 0; k < COMET_STEPS; k++) {
      const s1 = head - (k / COMET_STEPS) * COMET_TAIL
      const s0 = head - ((k + 1) / COMET_STEPS) * COMET_TAIL
      const seg = slicePolyline(tr.coords, tr.cum, s0, s1)
      if (!seg) continue
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: seg },
        properties: {
          hsi: tr.hsi,
          a: Math.pow(1 - k / COMET_STEPS, 1.6),
          // Головное звено — всегда с белой шапкой, даже когда голова ушла за
          // конец усика: звено там само сжимается зажимом в slicePolyline, и
          // шапка втягивается за край вместе с ним, а не гаснет скачком.
          tip: k === 0 ? 1 : 0,
        },
      })
    }
  }
  return { type: 'FeatureCollection', features }
}
